/**
 * IoC: KitContext + the context-request protocol (RFC §4a, NORMATIVE).
 *
 * Aligned with the WICG community context protocol so third-party elements
 * interoperate: a requester dispatches a composed, bubbling `"context-request"`
 * CustomEvent; a providing ancestor answers it synchronously during dispatch.
 *
 * NEAREST-provider-wins is the scoping rule (subtree overrides). To make the
 * nearest ancestor win, providers listen in the BUBBLE phase: the request
 * bubbles from the requester upward, so the closest providing ancestor hears it
 * first, calls `stopPropagation()`, and invokes the callback. (The RFC's sketch
 * says "capturing listener", but a capturing listener runs outermost-first and
 * would make the FURTHEST ancestor win — contradicting the twice-stated
 * nearest-wins rule and the WICG protocol. The semantics win; deviation noted.)
 *
 * An element that provides a token answers its OWN requests for it too (at-target
 * listeners fire regardless of phase) — consistent with the WICG protocol.
 *
 * Resolution modes:
 * - one-shot (`inject`): synchronous; throws `KitContextError` naming the token
 *   when no ancestor provides it (§4a connect-order guarantee: providers must be
 *   already-connected ancestors — the app shell provides at the composition root).
 * - subscription (`watch`): the provider retains the callback and re-invokes it
 *   when `provide()` is called again for the same token; the callback receives an
 *   `unsubscribe`, and the requesting KitContext tracks + releases all of its
 *   subscriptions on `dispose()` (KitElement disposes on disconnect).
 */

import type { Unsubscribe } from "./signal";

/** The event type of the WICG-aligned context protocol. */
export const CONTEXT_REQUEST = "context-request";

/** A typed context token. Identity-based: two tokens with the same name differ. */
export interface ContextToken<T> {
  readonly name: string;
  /** Phantom type carrier — never assigned. */
  readonly __type?: T;
}

/** Create a typed context token. */
export function createToken<T>(name: string): ContextToken<T> {
  return { name };
}

/** Thrown by `inject()` when no connected ancestor provides the token. */
export class KitContextError extends Error {
  readonly token: ContextToken<unknown>;

  constructor(token: ContextToken<unknown>) {
    super(
      `No provider found for context token "${token.name}". ` +
        `A providing ancestor must be connected BEFORE this element ` +
        `(the app shell provides at the composition root; see web-kit RFC §4a).`
    );
    this.name = "KitContextError";
    this.token = token;
  }
}

/** Callback invoked by a provider. `unsubscribe` is present in subscription mode. */
export type ContextCallback<T> = (value: T, unsubscribe?: Unsubscribe) => void;

/** `detail` shape of a `"context-request"` event. */
export interface ContextRequestDetail<T = unknown> {
  context: ContextToken<T>;
  callback: ContextCallback<T>;
  /** false/absent = one-shot resolution. */
  subscribe?: boolean;
}

/**
 * Low-level protocol primitive: dispatch a `context-request` from `host`.
 * Returns true when a provider answered (synchronously, during dispatch).
 */
export function requestContext<T>(
  host: EventTarget,
  token: ContextToken<T>,
  callback: ContextCallback<T>,
  subscribe = false
): boolean {
  let answered = false;
  const detail: ContextRequestDetail<T> = {
    context: token,
    callback: (value, unsubscribe) => {
      answered = true;
      callback(value, unsubscribe);
    },
    subscribe,
  };
  host.dispatchEvent(
    new CustomEvent<ContextRequestDetail<T>>(CONTEXT_REQUEST, {
      detail,
      bubbles: true,
      composed: true,
    })
  );
  return answered;
}

/** The IoC container seam (RFC §4a). One per host element, host-bound. */
export interface KitContext {
  /** Provide a value for this host's subtree (nearest-provider-wins). */
  provide<T>(token: ContextToken<T>, value: T): void;
  /** Resolve a token from the nearest providing ancestor. Throws `KitContextError`. */
  inject<T>(token: ContextToken<T>): T;
  /**
   * Subscription-mode inject: `fn` is called now and again whenever the provider
   * re-provides the token. Auto-released on `dispose()`; the returned function
   * releases early. Throws `KitContextError` when unresolved.
   */
  watch<T>(token: ContextToken<T>, fn: (value: T) => void): Unsubscribe;
  /** Release the provider listener and all subscription-mode unsubscribes. */
  dispose(): void;
}

/**
 * Create a KitContext bound to `host`. Providing installs ONE bubble-phase
 * `context-request` listener on the host (lazily, on first `provide`).
 */
export function createKitContext(host: EventTarget): KitContext {
  const values = new Map<ContextToken<unknown>, unknown>();
  const subscribers = new Map<ContextToken<unknown>, Set<ContextCallback<unknown>>>();
  const ownUnsubscribes = new Set<Unsubscribe>();
  let listening = false;

  const onRequest = (ev: Event) => {
    const { detail } = ev as CustomEvent<ContextRequestDetail>;
    if (!detail || !values.has(detail.context)) return; // not ours — let it bubble on
    ev.stopPropagation(); // nearest provider wins
    const token = detail.context;
    const value = values.get(token);
    if (detail.subscribe) {
      let subs = subscribers.get(token);
      if (!subs) {
        subs = new Set();
        subscribers.set(token, subs);
      }
      const { callback } = detail;
      subs.add(callback);
      const unsubscribe = () => subs.delete(callback);
      callback(value, unsubscribe);
    } else {
      detail.callback(value);
    }
  };

  return {
    provide<T>(token: ContextToken<T>, value: T): void {
      values.set(token, value);
      if (!listening) {
        listening = true;
        host.addEventListener(CONTEXT_REQUEST, onRequest);
      }
      // Re-provide: push the new value to subscription-mode consumers.
      const subs = subscribers.get(token);
      if (subs) {
        for (const cb of [...subs]) cb(value, () => subs.delete(cb));
      }
    },

    inject<T>(token: ContextToken<T>): T {
      let resolved: T | undefined;
      let hit = false;
      const answered = requestContext(host, token, (value) => {
        resolved = value;
        hit = true;
      });
      if (!answered || !hit) throw new KitContextError(token);
      return resolved as T;
    },

    watch<T>(token: ContextToken<T>, fn: (value: T) => void): Unsubscribe {
      let providerUnsub: Unsubscribe | undefined;
      const answered = requestContext<T>(
        host,
        token,
        (value, unsubscribe) => {
          providerUnsub = unsubscribe;
          fn(value);
        },
        true
      );
      if (!answered) throw new KitContextError(token);
      const release = () => {
        providerUnsub?.();
        ownUnsubscribes.delete(release);
      };
      ownUnsubscribes.add(release);
      return release;
    },

    dispose(): void {
      for (const release of [...ownUnsubscribes]) release();
      ownUnsubscribes.clear();
      if (listening) {
        host.removeEventListener(CONTEXT_REQUEST, onRequest);
        listening = false;
      }
      values.clear();
      subscribers.clear();
    },
  };
}
